import * as React from 'react';
import { provider, toIntent, createViewConfig, dynamic, assignProps } from '@sail/engine';
import { FormFieldGroup, InputWrapperConfig } from './FormFieldGroup';
import { NativeTextFieldConfig } from './TextField';
import { NativeSelectConfig, SelectConfig } from './Select';
import { FormFieldConfig } from './FormField';
import { css } from '../css';
import { NativeTextAreaConfig } from './TextArea';
export const CompositeFieldContext = /*#__PURE__*/ React.createContext(null);
/**
 * Remove focus within styles if a select inside of the fieldset is focused.
 * If someone clicks on anything inside of the fieldset other than an
 * interactive element, move the focus to the first input, otherwise move it to a select or button.
 */ const focusComposite = toIntent([
    assignProps((props)=>{
        const ref = React.useRef(null);
        const [showFocusIndicator, setShowFocusIndicator] = React.useState(!props.disabled);
        return {
            ref,
            tabIndex: -1,
            'data-focus-visible': showFocusIndicator,
            onFocus (e) {
                if (props.disabled) {
                    return;
                }
                if (ref.current && e.target === ref.current) {
                    var _ref_current;
                    const input = ref === null || ref === void 0 ? void 0 : (_ref_current = ref.current) === null || _ref_current === void 0 ? void 0 : _ref_current.querySelector('input');
                    if (input) {
                        // eslint-disable-next-line @sail/ui-no-reflow
                        input.focus();
                    } else {
                        var // eslint-disable-next-line @sail/ui-no-reflow
                        _ref_current_querySelector, _ref_current1;
                        (_ref_current1 = ref.current) === null || _ref_current1 === void 0 ? void 0 : (_ref_current_querySelector = _ref_current1.querySelector('select,button')) === null || _ref_current_querySelector === void 0 ? void 0 : _ref_current_querySelector.focus();
                    }
                }
            },
            onBlur (e) {
                if (props.disabled) {
                    return;
                }
                const element = e.relatedTarget;
                setShowFocusIndicator((element === null || element === void 0 ? void 0 : element.tagName) !== 'SELECT');
            }
        };
    }),
    dynamic((props)=>{
        if (!props['data-focus-visible']) {
            return [];
        }
        return [
            css({
                ':focus-within': {
                    focusRing: 'focus'
                }
            }),
            props.invalid ? css({
                ':focus-within': {
                    keyline: 'form.error.focused'
                }
            }) : css({
                ':focus-within': {
                    keyline: 'form.focused'
                }
            })
        ];
    })
]);
export const CompositeFieldConfig = createViewConfig({
    props: {},
    flattens: true,
    name: 'CompositeField',
    defaults: {
        // should match the defaults in FormFieldGroup
        size: 'medium'
    }
});
const selectStyles = {
    bleedX: 'small',
    bleedY: 'xsmall'
};
const selectLargeStyles = {
    bleedX: 'space.150',
    bleedY: 'small'
};
export const CompositeField = CompositeFieldConfig.createView(({ label, hiddenElements, invalid, children, ...props })=>{
    return /*#__PURE__*/ React.createElement(FormFieldGroup, {
        uses: [
            InputWrapperConfig.customize({
                uses: invalid ? [
                    assignProps({
                        invalid
                    })
                ] : []
            })
        ],
        ...props,
        "data-invalid": invalid,
        legend: label,
        hiddenElements: hiddenElements === null || hiddenElements === void 0 ? void 0 : hiddenElements.map((el)=>el === 'label' ? 'legend' : el)
    }, /*#__PURE__*/ React.createElement(SelectConfig.Customize, {
        css: props.size === 'large' ? selectLargeStyles : selectStyles
    }, children));
}, {
    subviews: {
        inputs: {
            css: {
                stack: 'x',
                gap: 'small',
                keyline: 'form',
                borderRadius: 'form',
                backgroundColor: 'form',
                color: 'form',
                font: 'body.small',
                transition: 'box-shadow 240ms',
                alignY: 'center',
                paddingY: 'xsmall',
                paddingX: 'small'
            },
            uses: [
                focusComposite
            ]
        }
    },
    forward: {
        disabled: true,
        size: true,
        invalid: true
    },
    variants: {
        size: {
            small: {
                subviews: {
                    inputs: {
                        css: {
                            fontSize: '13px',
                            lineHeight: '16px'
                        }
                    }
                }
            },
            large: {
                subviews: {
                    inputs: {
                        css: {
                            paddingY: 'small',
                            paddingX: 'space.150'
                        }
                    }
                }
            }
        },
        invalid: {
            true: {
                subviews: {
                    inputs: {
                        css: {
                            keyline: 'form.error'
                        }
                    }
                }
            }
        },
        disabled: {
            true: {
                subviews: {
                    inputs: {
                        css: {
                            color: 'form.disabled',
                            backgroundColor: 'form.disabled'
                        }
                    }
                }
            }
        }
    },
    uses: [
        FormFieldConfig.customize({
            defaults: {
                isInCompositeField: true
            }
        }),
        // this provides backwards compatibility with older inputs
        // new inputs leverage Context.customize
        provider(CompositeFieldContext, {
            isInCompositeField: true
        }),
        // resize native inputs to fit inside the composite field
        // and remove borders and box shadows
        dynamic(({ size })=>{
            return [
                NativeSelectConfig.customize({
                    css: {
                        // This unsets the minWidth: 0 that's used to prevent
                        // grid width blow-out in Select.
                        minWidth: 'initial',
                        width: 'auto',
                        ':not(:focus)': {
                            boxShadow: 'none'
                        },
                        ':hover:not(:disabled)': {
                            boxShadow: 'none'
                        }
                    },
                    defaults: {
                        size
                    }
                }),
                NativeTextFieldConfig.customize({
                    css: {
                        boxShadow: 'none',
                        ':focus': {
                            focusRing: 'none',
                            keyline: 'none'
                        },
                        padding: 'space.0',
                        borderRadius: 'none'
                    },
                    defaults: {
                        size
                    }
                }),
                NativeTextAreaConfig.customize({
                    defaults: {
                        size
                    }
                })
            ];
        }),
        // if the composite field has an error, make sure the input is marked as invalid
        assignProps(({ error })=>{
            return error ? {
                invalid: true
            } : {};
        })
    ]
});

//# sourceMappingURL=CompositeField.js.map